<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="pageTitle" :text="text" :text1="text1" />
        <div class="d-flex flex-wrap gap-2 align-items-center">
          <router-link
            to="/user-management/roles-permissions"
            class="btn btn-white border"
          >
            <i class="ti ti-arrow-left me-1"></i>Back to roles
          </router-link>
          <button
            v-if="canEdit && role"
            type="button"
            class="btn btn-primary"
            :disabled="saving || loading"
            @click="savePermissions"
          >
            <span
              v-if="saving"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            Save permissions
          </button>
        </div>
      </div>

      <div v-if="loading" class="card">
        <div class="card-body text-muted">
          <span class="spinner-border spinner-border-sm me-2"></span>
          Loading role permissions…
        </div>
      </div>

      <div v-else-if="!role" class="alert alert-warning">
        Role not found.
        <router-link to="/user-management/roles-permissions" class="alert-link">
          Return to roles list
        </router-link>
      </div>

      <template v-else>
        <div class="card mb-3">
          <div class="card-body d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div>
              <h5 class="mb-1">{{ role.name }}</h5>
              <p class="text-muted small mb-0">
                {{ selectedPermissions.length }} permission(s) selected.
                <span v-if="!canEdit" class="text-warning">
                  You have read-only access (Role.Edit required to save).
                </span>
              </p>
            </div>
            <div v-if="canEdit" class="d-flex gap-2">
              <button type="button" class="btn btn-sm btn-light" @click="selectAll">
                Select all
              </button>
              <button type="button" class="btn btn-sm btn-light" @click="clearAll">
                Clear all
              </button>
            </div>
          </div>
        </div>

        <div
          v-for="group in modulesByGroup"
          :key="group.key"
          class="card mb-3"
        >
          <div class="card-header">
            <h6 class="mb-0">{{ group.label }}</h6>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive dq-perms-tablewrap">
              <table class="table dq-perms-table mb-0">
                <thead>
                  <tr>
                    <th class="dq-sticky-col dq-module-col">Module</th>
                    <th
                      v-for="action in actions"
                      :key="action.key"
                      class="dq-action-col text-center"
                    >
                      <span class="dq-action-pill">{{ action.label }}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="mod in group.modules" :key="mod.key">
                    <td class="dq-sticky-col dq-module-cell">
                      <span class="dq-module-chip">
                        <i v-if="mod.icon" :class="mod.icon"></i>
                        {{ mod.label }}
                      </span>
                    </td>
                    <td
                      v-for="action in actions"
                      :key="action.key"
                      class="dq-checkcell text-center"
                    >
                      <label v-if="canEdit" class="dq-check mb-0">
                        <input
                          type="checkbox"
                          :checked="hasPermission(mod.key, action.key)"
                          @change="togglePermission(mod.key, action.key)"
                        />
                        <span class="dq-check-ui"></span>
                      </label>
                      <span v-else class="text-muted">
                        <i
                          v-if="hasPermission(mod.key, action.key)"
                          class="ti ti-check text-success"
                        ></i>
                        <span v-else>—</span>
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { useRoleStore } from "@/stores/roleStore";
import { useCatalogStore } from "@/stores/catalogStore";
import { useAuthStore } from "@/stores/authStore";
import { toast } from "@/utils/toast";

const route = useRoute();
const roleStore = useRoleStore();
const catalogStore = useCatalogStore();
const authStore = useAuthStore();

const text = "Administration";
const text1 = "Role permissions";

const loading = ref(true);
const saving = ref(false);
const selectedPermissions = ref([]);

const roleId = computed(() => Number(route.params.roleId));

const role = computed(() =>
  roleStore.roles.find((r) => Number(r.id) === roleId.value),
);

const pageTitle = computed(() =>
  role.value?.name ? `Permissions: ${role.value.name}` : "Role permissions",
);

const canEdit = computed(() => authStore.can("Role.Edit"));

const actions = computed(() => catalogStore.actions);
const modulesByGroup = computed(() => {
  const sortedGroups = [...(catalogStore.groups || [])].sort(
    (a, b) => (a.order || 0) - (b.order || 0),
  );
  return sortedGroups
    .map((group) => ({
      ...group,
      modules: (catalogStore.modules || []).filter(
        (m) => m.group === group.key,
      ),
    }))
    .filter((g) => g.modules.length > 0);
});

function permissionName(moduleKey, actionKey) {
  return `${catalogStore.normalizeModuleKey(moduleKey)}.${actionKey}`;
}

function hasPermission(moduleKey, actionKey) {
  return selectedPermissions.value.includes(
    permissionName(moduleKey, actionKey),
  );
}

function togglePermission(moduleKey, actionKey) {
  const name = permissionName(moduleKey, actionKey);
  const idx = selectedPermissions.value.indexOf(name);
  if (idx === -1) {
    selectedPermissions.value.push(name);
  } else {
    selectedPermissions.value.splice(idx, 1);
  }
}

function selectAll() {
  const all = [];
  for (const mod of catalogStore.modules) {
    for (const action of catalogStore.actions) {
      all.push(permissionName(mod.key, action.key));
    }
  }
  selectedPermissions.value = [...new Set(all)];
}

function clearAll() {
  selectedPermissions.value = [];
}

function loadRolePermissionsFromStore() {
  const r = role.value;
  if (!r) return;
  const names = (r.permissions || []).map((p) =>
    catalogStore.normalizePermissionName(p.name || p),
  );
  selectedPermissions.value = [...new Set(names.filter(Boolean))];
}

async function savePermissions() {
  if (!role.value || !canEdit.value) return;
  saving.value = true;
  try {
    await roleStore.assignPermissions(roleId.value, [
      ...selectedPermissions.value,
    ]);
    loadRolePermissionsFromStore();
  } catch (e) {
    toast(e?.response?.data?.message || "Could not save permissions.", "error");
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  loading.value = true;
  try {
    await catalogStore.fetchPermissionsCatalog();
    if (!roleStore.roles.length) {
      await roleStore.fetchRoles();
    }
    if (!role.value) {
      await roleStore.fetchRoles();
    }
    loadRolePermissionsFromStore();
  } catch (e) {
    toast(e?.message || "Failed to load role.", "error");
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.dq-perms-tablewrap {
  max-height: 520px;
  overflow: auto;
}
.dq-perms-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  font-size: 0.78rem;
  white-space: nowrap;
}
.dq-module-col {
  min-width: 200px;
}
.dq-action-col {
  min-width: 72px;
}
.dq-sticky-col {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
}
.dq-module-cell {
  font-weight: 600;
}
.dq-module-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.dq-action-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.05);
  font-weight: 600;
  font-size: 0.72rem;
}
.dq-check {
  display: inline-flex;
  cursor: pointer;
}
.dq-check input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.dq-check-ui {
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 2px solid rgba(15, 23, 42, 0.2);
  display: inline-block;
  vertical-align: middle;
}
.dq-check input:checked + .dq-check-ui {
  background: var(--bs-primary, #ff9f43);
  border-color: var(--bs-primary, #ff9f43);
  box-shadow: inset 0 0 0 2px #fff;
}
</style>
